import { forwardRef, useEffect, useRef, useState } from "react";
import { animated } from "react-spring";
import cx from "../../../../lib/cx.js";
import { springConfigs, useReveal } from "../../../../hooks/useReveal.jsx";
import { useMeasureWithNode } from "../../../../hooks/useMeasure.js";
import DefaultPortal from "../../../utils/DefaultPortal/DefaultPortal.jsx";
import Avatar from "../../Avatar/Avatar";
import { Logo } from "../../Logo/Logo.jsx";
import styles from "../dropdown.css.js";
import { usePosition, useZIndex } from "./Position.jsx";
import { useFitOnScreen } from "./FitOnScreen.jsx";
import { overlayStyles } from "./overlay.css.js";

const OverlayPlacer = ({ node, presenceProps, renderOverlay }) => {
  const [overlayNode, setOverlayNode] = useState();
  const anchorPosition = usePosition(node);
  const zIndex = useZIndex(node);
  const overlayDims = useMeasureWithNode(overlayNode);

  const fitProps = useFitOnScreen({
    overlayNode,
    preferredPlacement: "bottom",
    anchorPosition,
    distanceFromAnchor: 0,
    overlayDims,
  });

  if (!anchorPosition || !fitProps || zIndex === null) return null;

  return renderOverlay({
    ...fitProps,
    anchorZIndex: zIndex + 5,
    presenceProps,
    setNode: setOverlayNode,
  });
};

const ArrowOverlay = ({
  children,
  anchorZIndex,
  presenceProps,
  placement,
  className,
  setNode,
  style: fitStyle,
}) => {
  const xMod = placement === "left" ? 20 : placement === "right" ? -20 : 0;
  const yMod = placement === "top" ? 20 : placement === "bottom" ? -20 : 0;

  return (
    <animated.div
      className={overlayStyles.placer}
      style={{
        zIndex: anchorZIndex,
        opacity: presenceProps.value,
        transform: presenceProps.value.to(
          (val) =>
            `translate3d(${xMod * (1 - val)}px, ${yMod * (1 - val)}px, 0)`,
        ),
        pointerEvents: presenceProps.value.to((val) =>
          val > 0.9 ? "initial" : "none",
        ),
        ...fitStyle,
      }}
      ref={setNode}
    >
      <div className={cx(overlayStyles.defaultOverlay, className)}>
        {children}
      </div>
    </animated.div>
  );
};

const OpenDropdownButton = forwardRef(
  ({ me, onClick, isHovered, withLogo, avatarSize }, ref) => (
    <button
      type="button"
      className={cx(
        styles.openDropdownButton({ withLogo: withLogo }),
        isHovered ? styles.hover : "",
      )}
      onClick={onClick}
      ref={ref}
    >
      {withLogo && <Logo />}
      <Avatar user={me} size={avatarSize} />
    </button>
  ),
);

const OpenDropdown = ({
  overlayComponent: UserOverlay,
  overlayData,
  open,
  toggle,
  me,
  withLogo,
  avatarSize,
  ...rest
}) => {
  const nodeRef = useRef();
  const [overlayNode, setOverlayNode] = useState();

  const reveal = useReveal(open, { config: springConfigs.quick });

  const [isFixed, setIsFixed] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isTouch, setIsTouch] = useState(false);

  // Close on Escape or Click Outside
  useEffect(() => {
    if (!open || !overlayNode) return undefined;

    const handleHideDropdown = (event) => {
      if (event.key === "Escape") {
        if (isFixed) setIsFixed(false);
        if (isHovered) setIsHovered(false);
        toggle();
      }
    };
    const handleClickOutside = (event) => {
      if (
        overlayNode &&
        !overlayNode.contains(event.target) &&
        nodeRef.current &&
        !nodeRef.current.contains(event.target)
      ) {
        if (isFixed) setIsFixed(false);
        if (isHovered) setIsHovered(false);
        toggle();
      }
    };

    document.addEventListener("keydown", handleHideDropdown, true);
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("keydown", handleHideDropdown, true);
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [open, overlayNode, toggle, isFixed, isHovered]);

  const handleDropdownMouseLeave = () => {
    if (!isTouch && open && isHovered && !isFixed) {
      setIsHovered(false);
      toggle();
    }
  };

  const handleDropdownMouseEnter = () => {
    if (!isTouch && !open && !isFixed) {
      setIsHovered(true);
      toggle();
    }
  };

  const handleTouchStart = () => {
    setIsTouch(true);
  };

  const handleClick = () => {
    if (isHovered) {
      // If opened via hover, fix it on click
      setIsHovered(false);
      setIsFixed(true);
    } else if (isFixed) {
      // If already fixed, close it on click
      setIsFixed(false);
      toggle();
    } else {
      // Toggle normally (e.g. if hover failed or touch)
      setIsFixed(!open);
      toggle();
    }
  };

  const setNode = (node, overlayProps) => {
    if (node && node !== overlayNode) {
      overlayProps.setNode(node);
      setOverlayNode(node);
    }
  };

  return (
    <div
      onMouseEnter={handleDropdownMouseEnter}
      onMouseLeave={handleDropdownMouseLeave}
      onTouchStart={handleTouchStart}
    >
      <OpenDropdownButton
        {...rest}
        me={me}
        withLogo={withLogo}
        avatarSize={avatarSize}
        ref={nodeRef}
        onClick={handleClick}
        isHovered={isHovered || isFixed}
      />
      {reveal((props) => (
        <DefaultPortal>
          <OverlayPlacer
            node={nodeRef.current}
            presenceProps={props}
            renderOverlay={(overlayProps) => (
              <ArrowOverlay
                {...overlayProps}
                setNode={(node) => setNode(node, overlayProps)}
              >
                <UserOverlay
                  data={overlayData}
                  close={toggle}
                  withLogo={withLogo}
                />
              </ArrowOverlay>
            )}
          />
        </DefaultPortal>
      ))}
    </div>
  );
};

export default OpenDropdown;
